/**
 * The plainest search words, read without the model: "rent", "torpaq",
 * "продажа" must mean something even where the AI parser is off or
 * finds nothing (owner, 2026-10-05: "rent" gave nothing). The same
 * dictionary lives in the backend's magic_search.plain_params.
 *
 * Word forms and a capital first letter count too: "İpoteka", "Kupçalı",
 * "Квартиры" gave nothing, and plain "ev" / "evlər" (a home, most often
 * a flat) turned every search into houses (usage review, 2026-10-20).
 */
import { kindsOf } from "@/lib/property-kinds";

const PLAIN: Array<[string[], Record<string, string>]> = [
  [["rent", "rental", "monthly", "kirayə", "kiraye", "kirayəyə", "kirayeye", "icarə", "icare", "icarəyə",
    "arenda", "аренда", "аренду", "снять", "сниму", "сдаётся", "сдается"], { leased: "true" }],
  [["buy", "sale", "for sale", "satış", "satis", "satılır", "satilir", "satılıq", "satiliq", "alış", "almaq",
    "продажа", "продается", "продаётся", "купить", "куплю"], { leased: "false" }],
  [["land", "plot", "torpaq", "torpaqlar", "torpaq sahəsi", "участок", "участки", "земля", "землю"], { category: "land" }],
  [["house", "houses", "villa", "villalar", "həyət evi", "həyət evləri", "heyet evi", "bağ evi", "bağ evləri",
    "bag evi", "özəl ev", "fərdi ev", "дом", "коттедж", "дача"], { category: "house" }],
  // An A-frame is a house (the backend's a_frame tag, houses only).
  [["a-frame", "a frame", "aframe", "a-freym", "a freym", "afreym", "afrem", "a-frame ev", "a-frame evi",
    "а-фрейм", "а фрейм", "афрейм", "a-фрейм", "дом-шалаш", "дом шалаш"], { category: "house", tags: "a_frame" }],
  [["apartment", "apartments", "flat", "flats", "mənzil", "menzil", "mənzillər", "menziller", "квартира",
    "квартиры", "квартиру", "квартир"], { category: "apartment" }],
  [["mortgage", "ipoteka", "ipotekalı", "ipotekali", "ipotekaya", "ипотека", "ипотеку", "в ипотеку"], { has_mortgage: "yes" }],
  [["kupça", "kupca", "kupçalı", "kupcali", "kupçası", "çıxarış", "cixaris", "çıxarışlı", "купчая", "выписка"],
    { has_bill_of_sale: "yes" }],
  // Renovated or not ("Temirsiz" + Enter searched the descriptions for
  // the word, usage review 2026-10-20). The "no" words come first.
  [["təmirsiz", "temirsiz", "təmirsizdir", "без ремонта", "unrenovated", "not renovated"], { has_repair: "no" }],
  [["təmirli", "temirli", "təmirlidir", "с ремонтом", "renovated"], { has_repair: "yes" }],
  [["cheap", "ucuz", "ən ucuz", "дёшево", "дешево", "недорого"], { sort: "price" }],
  [["new build", "yeni tikili", "новостройка", "новостройки"], { listing_kind: "new" }],
  [["old build", "köhnə tikili", "вторичка"], { listing_kind: "old" }],
  // Commercial premises and garages (2026-10-21), by what they are.
  [["office", "offices", "ofis", "ofislər", "ofisler", "ofisi", "biznes mərkəzi", "офис", "офисы", "офиса"],
    { category: "commercial", property_kind: "office" }],
  // Everything else commercial is an obyekt, bina.az's word (owner,
  // 2026-10-21: two kinds, office and objects).
  [["obyekt", "obyektlər", "obyektler", "obyekti", "объект", "объекты", "обьект", "обьекты",
    "shop", "shops", "store", "mağaza", "magaza", "mağazalar", "magazalar", "mağazası", "butik", "dükan", "dukan",
    "магазин", "магазины", "магазина", "бутик",
    "restaurant", "cafe", "café", "restoran", "restoranlar", "kafe", "kafelər", "şadlıq sarayı", "sadliq sarayi",
    "ресторан", "кафе",
    "warehouse", "workshop", "anbar", "anbarlar", "sex", "sexlər", "istehsalat", "emalatxana", "avtoservis",
    "склад", "склады", "цех", "производство", "автосервис",
    "hotel", "otel", "mehmanxana", "hostel", "гостиница", "отель", "здание"],
    { category: "commercial", property_kind: "obyekt" }],
  [["commercial", "kommersiya", "qeyri-yaşayış", "qeyri yaşayış",
    "коммерческая", "коммерческое", "помещение", "нежилое"], { category: "commercial" }],
  [["parking", "parking space", "parkinq", "parkinq yeri", "avtodayanacaq", "dayanacaq yeri", "паркинг",
    "машиноместо", "машино-место", "парковочное место"], { category: "garage", property_kind: "parking" }],
  [["garage", "garages", "qaraj", "qarajlar", "qarajı", "гараж", "гаражи", "гаража"], { category: "garage" }],
];

/** Lower case that keeps Azerbaijani "İ" a plain "i": toLowerCase()
 *  turns it into "i" plus a combining dot, so "İpoteka" never matched. */
function lower(q: string): string {
  return q.replace(/İ/g, "i").toLowerCase();
}

// A home WITH a garage or a parking place ("qarajlı həyət evi", "дом с
// гаражом", "house with a garage") is that home plus the parking amenity;
// the garage category is for the garage or the parking place itself
// ("qaraj satılır", "garage for sale", "parking space"). The backend's
// magic_search.garage_as_amenity reads the same (ticket 63, 2026-10-30).
// Matched on the words with a space on each side.
const HOME_RE = new RegExp(
  "\\s(?:ev|evi|evin|evdə|evde|evdən|evden|evlər\\S*|evler\\S*|villa\\S*|mənzil\\S*|menzil\\S*|malikanə\\S*|daça"
  + "|дом|дома|доме|домик\\S*|коттедж\\S*|дач\\S*|квартир\\S*|особняк\\S*|таунхаус\\S*"
  + "|house\\S*|homes?|cottage\\S*|apartment\\S*|flats?|townhouse\\S*|bungalow\\S*)(?=\\s)",
  "u",
);
const WITH_GARAGE_RE = new RegExp([
  // "qarajlı", "parkinqli", "qarajla", "qaraj ilə", "qarajı da var", "maşın yeri olan"
  "\\s(?:qaraj|parkinq|parking|avtodayanacaq)l[ıiuü](?=\\s)",
  "\\s(?:qaraj|parkinq|parking)l[aə](?=\\s)",
  "\\s(?:qaraj|parkinq|parking|avtodayanacaq|dayanacaq yeri|maşın yeri|masin yeri)\\S* (?:da |də )?"
    + "(?:ilə|ile|var|olan|mövcud\\S*|movcud\\S*|daxil\\S*)(?=\\s)",
  // "с гаражом", "с ремонтом и паркингом", "гараж есть", "есть гараж"
  "\\sс (?:\\S+ ){0,3}(?:гараж|паркинг|парковк|парковочн|машиномест)",
  "\\s(?:гараж|паркинг|парковк|машиномест)\\S* (?:есть|имеется)(?=\\s)",
  "\\s(?:есть|имеется) (?:и |также )?(?:\\S+ )?(?:гараж|паркинг|парковк|машиномест)",
  // "with a garage", "with private parking", "has a garage", "garage included"
  "\\swith (?:\\S+ ){0,2}(?:garage|parking|carport|car park)",
  "\\s(?:has|have|having|plus|including|incl) (?:\\S+ ){0,2}(?:garage|parking)",
  "\\s(?:garage|parking) included(?=\\s)",
].join("|"), "u");

/** Words that name everything we list ("Bakı", "evlər", "elanlar"):
 *  no filter, and never a search of the descriptions for them. */
const EVERYTHING = new Set([
  "bakı", "baki", "baku", "bakıda", "bakida", "bakı şəhəri", "баку", "в баку",
  "ev", "evlər", "evler", "elan", "elanlar", "əmlak", "emlak", "daşınmaz əmlak",
  "homes", "home", "listings", "property", "недвижимость", "объявления",
]);

export function isEverythingQuery(q: string): boolean {
  return EVERYTHING.has(lower(q).trim().replace(/\s+/g, " "));
}

/** The filters the plain words name, or an empty object. */
export function plainParams(q: string): Record<string, string> {
  const text = ` ${lower(q).trim().replace(/\s+/g, " ")} `;
  const out: Record<string, string> = {};
  for (const [words, params] of PLAIN) {
    if (words.some((w) => text.includes(` ${w} `))) {
      for (const [k, v] of Object.entries(params)) if (!(k in out)) out[k] = v;
    }
  }
  // A kind belongs to its category: "mənzil mağaza yanında" is a flat.
  if (out.property_kind && !(kindsOf(out.category) as readonly string[]).includes(out.property_kind)) {
    delete out.property_kind;
  }
  // A home with a garage: the home its words name (none for a plain "ev"),
  // never the garage category, plus the parking amenity. A plot, a shop or a
  // garage carries no amenity tags, so none is added there.
  const bare = ` ${lower(q).replace(/[^\p{L}\p{N}\s-]/gu, " ").trim().replace(/\s+/g, " ")} `;
  if (HOME_RE.test(bare) && WITH_GARAGE_RE.test(bare)) {
    if (out.category === "garage") {
      const home = PLAIN.find(([ws, p]) => (p.category === "house" || p.category === "apartment") && ws.some((w) => bare.includes(` ${w} `)));
      if (home) out.category = home[1].category;
      else delete out.category;
      delete out.property_kind;
    }
    if (!out.category || out.category === "apartment" || out.category === "house") out.tags = "parking";
  }
  // Under the market value: "bazar qiymətindən 30% ucuz", "bazar
  // dəyərindən aşağı", "ниже рыночной на 20%": the verdict tiers, by how
  // far under (owner, 2026-10-11: three searches from the admin list).
  const market = /(^|\s)(bazar|market|рыноч)/.test(text) && /(^|\s)(ucuz|aşağı|asagi|endirim|below|under|ниже|дешевле)/.test(text);
  if (market) {
    const pct = text.match(/(\d{1,2})\s*(%|faiz|процент|percent)/);
    const n = pct ? Number(pct[1]) : 1;
    out.deal_tier = n >= 25 ? "exceptional" : n >= 15 ? "exceptional,great" : "exceptional,great,good";
    delete out.sort;
  }
  // "... olmasın": floors ruled out.
  if (/(birinci|1-?ci|ilk|aşağı) mərtəbə\S* (olmasın|olmamalıdır|istəmirəm|yox)|не (на )?перв\S* этаж|not (on )?(the )?(first|ground) floor/.test(text)) {
    out.exclude_first_floor = "true";
  }
  if (/(axırıncı|sonuncu|son|ən üst|üst) mərtəbə\S* (olmasın|olmamalıdır|istəmirəm|yox)|не (на )?последн\S* этаж|not (on )?(the )?(last|top) floor/.test(text)) {
    out.exclude_last_floor = "true";
  }
  // Rooms: "2 otaq", "3 rooms", "2 комнаты", "3 otaqlı".
  const rooms = text.match(/(?:^|\s)([1-6])\s*(?:otaq|otaqlı|rooms?|комнат\S*|комн\.?)/);
  if (rooms) out.rooms = rooms[1];
  // A price ceiling only with a price word or unit ("200 min", "200k",
  // "150000 azn", "до 1500", "300000-ə qədər"), or a bare five or six
  // digit number. "Amal2015" became a 2,015 AZN ceiling, a house number
  // after a street a price, and a pasted bina number (seven digits and
  // up) a 6.3 million chip (usage review, 2026-10-20).
  const PRICE_RE = /(?:^|\s)(?:(qədər|under|up to|до|max|maks|büdcə|budget)\s+)?(\d{1,3}(?:[\s.,]\d{3})+|\d+)\s*(min\S*|k|azn|manat|₼|тыс\S*)?(?:\s*(qədər|-?(?:ə|a|yə|ya) qədər|-?dək|dək))?(?=\s|$)/g;
  for (const m of text.matchAll(PRICE_RE)) {
    const [, before, num, unit, after] = m;
    const digits = num.replace(/[\s.,]/g, "");
    const thousand = !!unit && /^(min|k|тыс)/.test(unit);
    const cue = !!(before || unit || after);
    const n = Number(digits) * (thousand ? 1000 : 1);
    const bareOk = !cue && digits.length >= 5 && digits.length <= 6;
    if ((cue || bareOk) && n >= 100) {
      out.price_max = String(n);
      break;
    }
  }
  return out;
}
